import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getEmbedUrl, getYoutubeThumb } from "@/lib/video";
import { motion } from "framer-motion";
import { Play, ExternalLink, Search, Film } from "lucide-react";

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("All");
  const [open, setOpen] = useState(null);

  useEffect(() => { api.get("/videos").then((r) => setVideos(r.data)); }, []);

  const categories = useMemo(() => {
    const set = new Set(videos.map((v) => v.category || "General"));
    return ["All", ...Array.from(set)];
  }, [videos]);

  const filtered = useMemo(() => videos.filter((v) => {
    const matchesCat = category === "All" || (v.category || "General") === category;
    const matchesQ = !q || v.title.toLowerCase().includes(q.toLowerCase()) || (v.description || "").toLowerCase().includes(q.toLowerCase());
    return matchesCat && matchesQ;
  }), [videos, q, category]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-12 lg:py-16">
      <p className="overline text-primary mb-2">Video library</p>
      <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight">Watch & learn</h1>
      <p className="text-muted-foreground mt-2 max-w-xl">Curated videos from Evan on breath, performance, and nervous system control.</p>

      <div className="mt-8 flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`videos-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
              className={`text-xs font-medium px-4 py-2 rounded-full border transition-colors ${
                category === c ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:border-primary/40"
              }`}
            >{c}</button>
          ))}
        </div>
        <div className="relative max-w-xs w-full">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search videos..." className="pl-9 h-10 rounded-full" data-testid="videos-search-input" />
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card className="mt-10 p-12 text-center">
          <Film className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="mt-4 text-muted-foreground text-sm">No videos posted yet. Check back soon.</p>
        </Card>
      ) : (
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((v, i) => {
            const thumb = v.thumbnail_url || getYoutubeThumb(v.url);
            const embed = getEmbedUrl(v.url);
            return (
              <motion.div key={v.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <Card className="overflow-hidden h-full flex flex-col hover:border-primary/40 transition-colors" data-testid={`video-card-${v.id}`}>
                  <button
                    onClick={() => embed ? setOpen(v) : window.open(v.url, "_blank", "noopener")}
                    className="relative block w-full aspect-video bg-muted overflow-hidden group"
                    data-testid={`video-play-${v.id}`}
                  >
                    {thumb ? (
                      <img src={thumb} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-secondary/30">
                        <Film className="h-10 w-10 text-primary/50" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/15 transition-colors flex items-center justify-center">
                      <span className="inline-flex h-14 w-14 rounded-full bg-white/90 text-primary items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        {embed ? <Play className="h-6 w-6 fill-current" /> : <ExternalLink className="h-5 w-5" />}
                      </span>
                    </div>
                  </button>
                  <div className="p-5 flex-1 flex flex-col">
                    <Badge variant="secondary" className="text-xs self-start mb-3">{v.category || "General"}</Badge>
                    <h3 className="font-display text-lg font-semibold tracking-tight">{v.title}</h3>
                    {v.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{v.description}</p>}
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden" data-testid="video-player-dialog">
          <DialogHeader className="px-6 pt-6 pb-3">
            <DialogTitle className="font-display tracking-tight">{open?.title}</DialogTitle>
          </DialogHeader>
          {open && (
            <div className="aspect-video w-full bg-black">
              <iframe
                src={getEmbedUrl(open.url)}
                title={open.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          )}
          {open?.description && <p className="text-sm text-muted-foreground px-6 py-4">{open.description}</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
